"use client";

import * as React from "react";
import { Link } from "react-router-dom";
import { PhoneCall, MapPin, Instagram, Mail, Facebook, Linkedin, Twitter, ArrowRight, Heart } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import awloLogo from "../assets/AWLO1.png"; // Logo original couleur

const defaultAddress = {
  line1: "Cocody Riviera 3",
  line2: "Abidjan, Côte d'Ivoire",
  city: "Abidjan",
  country: "Côte d'Ivoire",
};

export default function Footer({
  className,
  companyName = "AWLO",
  description = "La plateforme immobilière nouvelle génération en Côte d'Ivoire. Achetez, vendez et louez en toute confiance.",
  email = "contact@awlo-ci.com",
  phone = "+225 07 57 59 09 84",
  address = defaultAddress,
  social = { 
    linkedin: "https://www.linkedin.com/company/immoci", 
    instagram: "https://instagram.com/awlo-ci",
    facebook: "#",
    twitter: "#"
  },
  onSubscribe,
}) {
  const [submitting, setSubmitting] = React.useState(false);
  const [newsletterEmail, setNewsletterEmail] = React.useState("");

  async function handleSubscribe(e) {
    e.preventDefault();
    if (!newsletterEmail) {
      toast.error("Veuillez saisir une adresse email valide.");
      return;
    }
    try {
      setSubmitting(true);
      if (onSubscribe) await onSubscribe(newsletterEmail);
      else await new Promise((r) => setTimeout(r, 600));
      toast.success("Inscription réussie. Bienvenue !");
      setNewsletterEmail("");
    } catch {
      toast.error("Échec de l'inscription.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <footer className={cn("relative w-full bg-white pt-24 pb-12 overflow-hidden border-t border-gray-100", className)}>
      
      {/* --- BACKGROUND ARTISTIQUE "SILK & NOISE" --- */}
      <div className="absolute inset-0 pointer-events-none">
         {/* 1. Texture Grain (Papier Luxe) - Très subtil */}
         <div className="absolute inset-0 opacity-[0.03] mix-blend-multiply z-0" 
              style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.8' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")` }} 
         />

         {/* 2. Lumières Diffuses (Ambiance) */}
         <div className="absolute -bottom-[20%] left-[10%] w-[500px] h-[500px] bg-orange-100/40 rounded-full blur-[120px] opacity-60" />
         <div className="absolute top-[20%] right-[-10%] w-[400px] h-[400px] bg-blue-50/50 rounded-full blur-[100px] opacity-50" />
      </div>

      <div className="container relative z-10 px-6 md:px-10 max-w-7xl mx-auto">
        
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 mb-20">
          
          {/* COLONNE 1 : MARQUE MASSIVE (Largeur 5) */}
          <div className="lg:col-span-5 space-y-8">
            <Link to="/" className="inline-block transition-transform hover:scale-105 duration-500 origin-left">
               {/* Logo agrandi */}
               <img src={awloLogo} alt="AWLO" className="h-20 w-auto object-contain" />
            </Link>
            
            <p className="text-gray-500 text-base leading-relaxed max-w-md font-light">
              {description}
            </p>

            {/* Social Links Minimalistes */}
            <div className="flex gap-4 pt-4">
              {[
                { icon: Instagram, href: social.instagram },
                { icon: Linkedin, href: social.linkedin },
                { icon: Facebook, href: social.facebook },
                { icon: Twitter, href: social.twitter }
              ].map((item, i) => (
                <a 
                  key={i} 
                  href={item.href} 
                  target="_blank" 
                  rel="noreferrer"
                  className="group relative flex items-center justify-center w-12 h-12 rounded-2xl bg-gray-50 hover:bg-[#FF9F68] transition-all duration-300 shadow-sm hover:shadow-orange-200"
                >
                  <item.icon className="h-5 w-5 text-gray-400 group-hover:text-white transition-colors duration-300" />
                </a>
              ))}
            </div>
          </div>

          {/* COLONNE 2 : CONTACT DESIGN (Largeur 3) */}
          <div className="lg:col-span-3 space-y-8 pt-4">
            <h4 className="text-gray-900 font-bold text-sm uppercase tracking-widest">Nous trouver</h4>
            
            <div className="space-y-6">
              <a href={`tel:${phone.replace(/[^+\d]/g, "")}`} className="flex items-start gap-4 group">
                <div className="p-3 bg-white rounded-xl border border-gray-100 shadow-sm group-hover:border-[#FF9F68] group-hover:shadow-md transition-all duration-300">
                  <PhoneCall className="h-5 w-5 text-[#FF9F68]" />
                </div>
                <div>
                  <p className="text-xs text-gray-400 font-medium mb-1">Appelez-nous</p>
                  <p className="text-gray-700 font-semibold group-hover:text-[#FF9F68] transition-colors">{phone}</p>
                </div>
              </a>

              <a href={`mailto:${email}`} className="flex items-start gap-4 group">
                <div className="p-3 bg-white rounded-xl border border-gray-100 shadow-sm group-hover:border-[#FF9F68] group-hover:shadow-md transition-all duration-300">
                  <Mail className="h-5 w-5 text-[#FF9F68]" />
                </div>
                <div>
                  <p className="text-xs text-gray-400 font-medium mb-1">Écrivez-nous</p>
                  <p className="text-gray-700 font-semibold group-hover:text-[#FF9F68] transition-colors">{email}</p>
                </div>
              </a>

              <div className="flex items-start gap-4 group cursor-default">
                <div className="p-3 bg-white rounded-xl border border-gray-100 shadow-sm">
                  <MapPin className="h-5 w-5 text-[#FF9F68]" />
                </div>
                <div>
                  <p className="text-xs text-gray-400 font-medium mb-1">Visitez-nous</p>
                  <p className="text-gray-700 font-semibold">{address.city}, {address.country}</p>
                </div>
              </div>
            </div>
          </div>

          {/* COLONNE 3 : NEWSLETTER CARD (Largeur 4) */}
          <div className="lg:col-span-4 pt-2">
            <div className="relative bg-gradient-to-br from-gray-900 to-gray-800 rounded-[2rem] p-8 shadow-2xl overflow-hidden group">
              {/* Effet de fond sur la carte */}
              <div className="absolute top-0 right-0 w-32 h-32 bg-[#FF9F68] rounded-full blur-[60px] opacity-20 group-hover:opacity-30 transition-opacity duration-500" />
              
              <h4 className="relative text-white text-xl font-bold mb-3">Newsletter</h4>
              <p className="relative text-gray-400 text-sm mb-6 leading-relaxed">
                Les opportunités d'exception ne restent pas longtemps. Soyez notifié en premier.
              </p>
              
              <form onSubmit={handleSubscribe} className="relative space-y-3">
                <div className="relative">
                  <Input
                    type="email"
                    placeholder="Votre adresse email"
                    value={newsletterEmail}
                    onChange={(e) => setNewsletterEmail(e.target.value)}
                    className="bg-white/10 border-white/10 focus:bg-white/20 focus:border-[#FF9F68]/50 text-white placeholder:text-gray-500 h-12 rounded-xl backdrop-blur-sm transition-all"
                    disabled={submitting}
                  />
                </div>
                <Button 
                  type="submit" 
                  disabled={submitting}
                  className="w-full bg-[#FF9F68] hover:bg-[#E67E4F] text-white rounded-xl h-12 font-semibold shadow-lg shadow-orange-900/20 transition-all flex items-center justify-center gap-2 group/btn"
                >
                  {submitting ? "Inscription..." : (
                    <>
                      S'inscrire
                      <ArrowRight className="h-4 w-4 transition-transform group-hover/btn:translate-x-1" />
                    </>
                  )}
                </Button>
              </form>
            </div>
          </div>

        </div>

        {/* --- BOTTOM BAR --- */}
        <div className="border-t border-gray-200 pt-8 flex flex-col-reverse md:flex-row justify-between items-center gap-6">
          <p className="text-xs text-gray-400 font-medium">
            © {new Date().getFullYear()} {companyName}. Tous droits réservés.
          </p>
          
          <div className="flex gap-8">
            <Link to="/confidentialite" className="text-xs text-gray-500 hover:text-[#FF9F68] transition-colors font-medium">Confidentialité</Link>
            <Link to="/cgu" className="text-xs text-gray-500 hover:text-[#FF9F68] transition-colors font-medium">CGU</Link>
            <Link to="/mentions-legales" className="text-xs text-gray-500 hover:text-[#FF9F68] transition-colors font-medium">Mentions légales</Link>
          </div>

          <div className="flex items-center gap-1.5 text-xs text-gray-400">
            <span>Made with</span>
            <Heart className="h-3 w-3 text-red-500 fill-current animate-pulse" />
            <span>by</span>
            <a href="https://sync-ai.studio" target="_blank" rel="noreferrer" className="text-gray-900 hover:text-[#FF9F68] font-bold transition-colors">
              Sync
            </a>
          </div>
        </div>

      </div>
    </footer>
  );
}